import { MutableRefObject, useContext, useEffect, useRef, useState } from 'react'
import { Analytics } from 'aws-amplify'
import { v4 } from 'uuid'

import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import { VideoJsPlayer } from 'video.js'
import * as queryString from 'query-string'
import { useLocation } from 'react-router'
import { AuthContext } from '../../../../../../../../../../common/providers/AuthStatusProvider'
import {
  GetApplicationConfigurationQuery,
  SetVideoViewedByMutation,
  SetVideoViewedByMutationVariables,
} from '../../../../../../../../../../API'
import { setVideoViewedBy } from '../../../../../../../../../../graphql/mutations'
import CognitoApolloClient from '../../../../../../../../../../common/clients/CognitoApolloClient'
import IamApolloClient from '../../../../../../../../../../common/clients/IamApolloClient'
import build from '../../../../../../../../../../common/services/BuildNumber'
import { addVideoPlaylistProgress } from '../../../../../../../../common/redux/store/chat-actions'
import { useDispatch } from 'react-redux'

interface IAnalyticsPayload {
  viewId: string
  segmentId: string
  videoId: string
  customerId: string
  eventTimestamp: string // iso8601
  platform: 'web' | 'ios' | 'android'
  appVersion: string
  state: 'start' | 'playing' | 'speed change' | 'scrubbing' | 'end'
  startTimePointMS: number
  endTimePointMS: number | null
  speed: number | null
}

const useVideoAnalytics = (
  videoId: string,
  appConfigData: GetApplicationConfigurationQuery | undefined,
  playerRef: MutableRefObject<VideoJsPlayer | null>,
  videoOwnerId: string,
) => {
  const auth = useContext(AuthContext)
  const location = useLocation()
  const dispatch = useDispatch()

  const startTimePointMS = useRef<number | null>(0)
  const endTimePointMS = useRef<number | null>(null)
  const isStarted = useRef(false)
  const viewId = useRef('')
  const segmentId = useRef('')

  const [visible, setVisible] = useState<boolean>(false)
  const backdropRef = useRef<MutableRefObject<any>>(null)
  const postIdQueryParam = queryString.parse(location.search)?.postId

  const onLoadedMetadata = () => {
    setVisible(true)
  }

  const onPlayerReady = (player: VideoJsPlayer | null) => {
    playerRef.current = player
    player?.on('loadedmetadata', () => {
      onLoadedMetadata()
      // player?.currentTime()
    })
  }

  const [setVideoViewedByMutation] = useMutation<SetVideoViewedByMutation, SetVideoViewedByMutationVariables>(
    gql(setVideoViewedBy),
    {
      fetchPolicy: 'no-cache',
      client: !auth.user?.pending && auth.isAuthenticated ? CognitoApolloClient : IamApolloClient,
    },
  )

  useEffect(() => {
    viewId.current = v4()
    segmentId.current = v4()
  }, [])

  const handleStartAnalytics = async (userId?: string | null) => {
    startTimePointMS.current = 0
    endTimePointMS.current = null
    const o: IAnalyticsPayload = {
      viewId: viewId.current,
      videoId,
      appVersion: build,
      state: 'start',
      platform: 'web',
      speed: 1,
      eventTimestamp: new Date().toISOString(),
      segmentId: segmentId.current,
      startTimePointMS: 0,
      endTimePointMS: null,
      customerId: userId || '',
    }
    await setVideoViewedByMutation({
      variables: {
        input: {
          id: videoId,
          name: auth.user?.email || undefined,
          guestCustomerId: auth.isAuthenticated ? undefined : userId,
        },
      },
    })
    if (appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose) {
      console.log('START OBJECT', o)
      await Analytics.record(
        {
          partitionKey: '', // TODO check with the backend
          data: o,
          streamName: appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose,
        },
        'AWSKinesisFirehose',
      )
    }
  }

  const handlePlayingAnalytics = async (userId?: string | null) => {
    startTimePointMS.current = endTimePointMS.current || 0
    endTimePointMS.current = playerRef.current?.currentTime() || 0
    if (Math.abs(startTimePointMS.current - endTimePointMS.current) > 5) {
      segmentId.current = v4()
      const o: IAnalyticsPayload = {
        viewId: viewId.current,
        videoId,
        appVersion: build,
        state: 'scrubbing',
        platform: 'web',
        speed: null,
        eventTimestamp: new Date().toISOString(),
        segmentId: segmentId.current,
        startTimePointMS: startTimePointMS.current * 1000,
        endTimePointMS: endTimePointMS.current * 1000,
        customerId: userId || '',
      }
      if (appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose) {
        console.log('------SCRUBBING------', o)
        await Analytics.record(
          {
            partitionKey: '',
            data: o,
            streamName: appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose,
          },
          'AWSKinesisFirehose',
        )
      }
      return
    }
    const o: IAnalyticsPayload = {
      viewId: viewId.current,
      videoId,
      appVersion: build,
      state: 'playing',
      platform: 'web',
      speed: 1,
      eventTimestamp: new Date().toISOString(),
      segmentId: segmentId.current,
      startTimePointMS: startTimePointMS.current * 1000,
      endTimePointMS: endTimePointMS.current * 1000,
      customerId: userId || '',
    }
    if (appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose) {
      console.log('-------PLAYING--------', o)
      await Analytics.record(
        {
          partitionKey: '',
          data: o,
          streamName: appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose,
        },
        'AWSKinesisFirehose',
      )
    }
  }

  const handleEndAnalytics = async (userId?: string | null) => {
    startTimePointMS.current = endTimePointMS.current
    endTimePointMS.current = playerRef.current?.duration() || 0
    const o: IAnalyticsPayload = {
      viewId: viewId.current,
      videoId,
      appVersion: build,
      state: 'end',
      platform: 'web',
      speed: 1,
      eventTimestamp: new Date().toISOString(),
      segmentId: segmentId.current,
      startTimePointMS: (startTimePointMS.current || 0) * 1000,
      endTimePointMS: endTimePointMS.current * 1000,
      customerId: userId || '',
    }
    if (appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose) {
      console.log('---END EVENT---', o)
      await Analytics.record(
        {
          partitionKey: '',
          data: o,
          streamName: appConfigData?.getApplicationConfiguration?.videoPlayerEventsFirehose,
        },
        'AWSKinesisFirehose',
      )
    }
  }

  useEffect(() => {
    const player = playerRef.current
    if (!player) return
    let playInterval: number

    player.on('seeking', () => {
      if (postIdQueryParam && typeof postIdQueryParam === 'string') {
        dispatch(addVideoPlaylistProgress(Number(player?.currentTime().toFixed()), postIdQueryParam))
      }
    })

    player.on('playing', () => {
      if (playInterval) {
        window.clearInterval(playInterval)
      }
      if (!isStarted.current && auth.user?.id !== videoOwnerId) {
        handleStartAnalytics(
          auth.user?.id || sessionStorage.getItem('guestCustomerId') || localStorage.getItem('guestCustomerId'),
        )
      }
      isStarted.current = true
      playInterval = window.setInterval(() => {
        if (postIdQueryParam && typeof postIdQueryParam === 'string') {
          dispatch(addVideoPlaylistProgress(Number(player?.currentTime().toFixed()), postIdQueryParam))
        }
        if (auth.user?.id !== videoOwnerId) {
          handlePlayingAnalytics(
            auth.user?.id || sessionStorage.getItem('guestCustomerId') || localStorage.getItem('guestCustomerId'),
          )
        }
      }, 1000)
    })

    player.on('pause', () => {
      startTimePointMS.current = player.currentTime()
      if (playInterval) {
        window.clearInterval(playInterval)
      }
    })
    player.on('ended', () => {
      if (playInterval) {
        if (auth.user?.id !== videoOwnerId) {
          handleEndAnalytics(
            auth.user?.id || sessionStorage.getItem('guestCustomerId') || localStorage.getItem('guestCustomerId'),
          )
        }

        window.clearInterval(playInterval)
      }
    })

    return () => {
      window.clearInterval(playInterval)
    }
  }, [playerRef, auth.user?.id, visible])

  return {
    onPlayerReady,
    visible,
    backdropRef,
  }
}

export type UseVideoAnalytics = ReturnType<typeof useVideoAnalytics>

export default useVideoAnalytics
